import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { processTriggerService } from "@/services/process-trigger/process-trigger.service";
import type {
  ApplicationTriggerLinks,
  ListTriggerRunsParams,
  ProcessTrigger,
  TriggerRun,
  UpsertProcessTriggerInput,
} from "@/types/process-trigger.types";

const keys = {
  forProcess: (processId: string) => ["process-triggers", processId],
  mine: ["trigger-runs", "mine"],
  run: (id: string) => ["trigger-runs", "run", id],
  forApplication: (id: string) => ["trigger-runs", "application", id],
  list: (params: ListTriggerRunsParams) => ["trigger-runs", "list", params],
};

export const useProcessTriggers = (processId: string) =>
  useQuery<ProcessTrigger[]>({
    queryKey: keys.forProcess(processId),
    queryFn: async () => (await processTriggerService.getForProcess(processId)).data,
    enabled: !!processId,
  });

export const useSaveProcessTrigger = (processId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: UpsertProcessTriggerInput }) =>
      id
        ? processTriggerService.update(id, input)
        : processTriggerService.create(processId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.forProcess(processId) }),
  });
};

export const useDeleteProcessTrigger = (processId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => processTriggerService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.forProcess(processId) }),
  });
};

// Past its deadline a run can't be submitted, even before the server marks it expired.
export const isRunOpen = (run: TriggerRun) =>
  !run.dueAt || new Date(run.dueAt).getTime() > Date.now();

export const useMyTriggerRuns = (enabled = true) =>
  useQuery<TriggerRun[]>({
    queryKey: keys.mine,
    queryFn: async () => (await processTriggerService.getMine()).data,
    enabled,
    // Kept fresh so the To do tab appears, and its badge updates, without a reload.
    refetchInterval: 60_000,
    select: (runs) => runs.filter(isRunOpen),
  });

export const useTriggerRun = (id: string) =>
  useQuery<TriggerRun>({
    queryKey: keys.run(id),
    queryFn: async () => (await processTriggerService.getRun(id)).data,
    enabled: !!id,
    retry: false,
  });

export const useApplicationTriggerLinks = (applicantProcessId: string) =>
  useQuery<ApplicationTriggerLinks>({
    queryKey: keys.forApplication(applicantProcessId),
    queryFn: async () =>
      (await processTriggerService.getForApplication(applicantProcessId)).data,
    enabled: !!applicantProcessId,
    retry: false,
  });

export const useTriggerRunList = (params: ListTriggerRunsParams) =>
  useQuery<{ data: TriggerRun[]; meta: { total: number; totalPages: number; page: number } }>({
    queryKey: keys.list(params),
    queryFn: () => processTriggerService.list(params),
  });

// Every run mutation can change the to-do list, panels and the admin list.
const useRunMutation = <T,>(fn: (args: T) => Promise<unknown>) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trigger-runs"] }),
  });
};

export const useDeclineTriggerRun = () =>
  useRunMutation(({ id, reason }: { id: string; reason?: string }) =>
    processTriggerService.decline(id, reason)
  );

export const useCancelTriggerRun = () =>
  useRunMutation(({ id, reason }: { id: string; reason?: string }) =>
    processTriggerService.cancel(id, reason)
  );

export const useResendTriggerRun = () =>
  useRunMutation((id: string) => processTriggerService.resend(id));
